import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import api from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { AlertTriangle, Gift, ExternalLink, ArrowRight, Search, Megaphone, CalendarDays, ChevronLeft, ChevronRight, MapPin, UserPlus } from "lucide-react";

export default function Home() {
  const { t, i18n } = useTranslation();
  const [campaigns, setCampaigns] = useState([]);
  const [events, setEvents] = useState([]);
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    api.get("/campaigns?active_only=true").then(({ data }) => setCampaigns(data.slice(0, 6))).catch(() => {});
    api.get("/events").then(({ data }) => {
      const now = new Date();
      const upcoming = data.filter(e => new Date(e.end_date) >= now).slice(0, 6);
      setEvents(upcoming);
    }).catch(() => {});
  }, []);

  // Auto-advance slider every 6s
  useEffect(() => {
    if (campaigns.length < 2) return;
    const id = setInterval(() => setSlide(s => (s + 1) % campaigns.length), 6000);
    return () => clearInterval(id);
  }, [campaigns.length]);

  const quickAccess = [
    { to: "/incidencies", icon: AlertTriangle, title: t("nav.incidents"), desc: t("home.qa_incident_desc"), tone: "primary", tid: "qa-incident" },
    { to: "/targeta-regal", icon: Gift, title: t("nav.giftcard"), desc: t("home.qa_gift_desc"), tone: "accent", tid: "qa-giftcard" },
    { to: "/consulta-saldo", icon: Search, title: t("nav.check"), desc: t("home.qa_check_desc"), tone: "muted", tid: "qa-check" },
    { to: "/registrat", icon: UserPlus, title: "Uneix-t'hi", desc: "Rep novetats i promocions dels teus interessos", tone: "accent", tid: "qa-citizen" },
    { to: "/login", icon: ArrowRight, title: t("nav.login"), desc: t("home.qa_login_desc"), tone: "muted", tid: "qa-login" },
  ];

  return (
    <PublicLayout>
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 grain-bg opacity-70 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 pt-16 sm:pt-24 pb-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
              <div className="overline text-accent" data-testid="hero-overline">{t("home.overline")}</div>
              <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-heading font-bold tracking-tighter text-foreground leading-[1.05]">
                {t("home.title_1")}<br />
                <span className="text-primary">{t("home.title_2")}</span>
              </h1>
              <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed">{t("home.subtitle")}</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button asChild size="lg" className="rounded-full h-12 px-6 shadow-lg shadow-primary/20" data-testid="hero-cta-incident">
                  <Link to="/incidencies"><AlertTriangle className="w-4 h-4 mr-2" aria-hidden="true" /> {t("home.cta_incident")}</Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="rounded-full h-12 px-6 border-2" data-testid="hero-cta-giftcard">
                  <Link to="/targeta-regal"><Gift className="w-4 h-4 mr-2" aria-hidden="true" /> {t("home.cta_giftcard")}</Link>
                </Button>
                <Button asChild size="lg" variant="ghost" className="rounded-full h-12 px-6" data-testid="hero-cta-web">
                  <a href="https://www.shoppingsalou.com" target="_blank" rel="noreferrer">
                    <ExternalLink className="w-4 h-4 mr-2" aria-hidden="true" /> {t("home.cta_web")}
                  </a>
                </Button>
              </div>
            </motion.div>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }} className="lg:col-span-5">
            <div className="relative rounded-3xl overflow-hidden aspect-[4/5] shadow-2xl shadow-primary/20 border-2 border-border/60" data-testid="hero-image">
              <img src="https://images.unsplash.com/photo-1598306101262-4de4e1c2d4c8?w=800&auto=format&fit=crop" alt="" className="w-full h-full object-cover" />
              <div className="absolute bottom-4 left-4 right-4 glass rounded-2xl px-4 py-3 border border-white/40">
                <div className="text-xs uppercase tracking-widest text-primary font-semibold">{t("home.hero_badge")}</div>
                <div className="font-heading font-bold text-lg">{t("home.hero_badge_2")}</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CAMPAIGN SLIDER */}
      {campaigns.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-8" data-testid="campaign-slider-section">
          <div className="relative rounded-3xl overflow-hidden border-2 border-border/60 shadow-xl shadow-primary/10 bg-card">
            <div className="relative aspect-[16/6] md:aspect-[16/5]">
              {campaigns.map((c, i) => (
                <div key={c.id} aria-hidden={i !== slide} className={`absolute inset-0 transition-opacity duration-700 ${i === slide ? "opacity-100" : "opacity-0 pointer-events-none"}`} data-testid={`slider-slide-${i}`}>
                  {c.image_url ? <img src={c.image_url} alt="" className="absolute inset-0 w-full h-full object-cover" /> : <div className="absolute inset-0 bg-primary" />}
                  <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-transparent" />
                  <div className="relative h-full flex flex-col justify-end p-6 sm:p-10 text-white max-w-2xl">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-accent font-semibold flex items-center gap-2">
                      <Megaphone className="w-3 h-3" aria-hidden="true" /> {t("home.campaign_tag")}
                    </div>
                    <h3 className="font-heading font-bold text-2xl sm:text-4xl mt-2 leading-tight">{c.title}</h3>
                    <p className="text-sm sm:text-base opacity-90 mt-2 line-clamp-2">{c.description}</p>
                  </div>
                </div>
              ))}
            </div>
            {campaigns.length > 1 && (
              <>
                <button onClick={() => setSlide((slide - 1 + campaigns.length) % campaigns.length)} className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white text-primary shadow flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-primary" data-testid="slider-prev" aria-label="Anterior">
                  <ChevronLeft className="w-5 h-5" aria-hidden="true" />
                </button>
                <button onClick={() => setSlide((slide + 1) % campaigns.length)} className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white text-primary shadow flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-primary" data-testid="slider-next" aria-label="Següent">
                  <ChevronRight className="w-5 h-5" aria-hidden="true" />
                </button>
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5" role="tablist" aria-label="Slider paginació">
                  {campaigns.map((_, i) => (
                    <button key={i} onClick={() => setSlide(i)} aria-label={`Slide ${i + 1}`} aria-selected={i === slide} role="tab" data-testid={`slider-dot-${i}`} className={`h-1.5 rounded-full transition-all ${i === slide ? "w-8 bg-white" : "w-1.5 bg-white/50"}`} />
                  ))}
                </div>
              </>
            )}
          </div>
        </section>
      )}

      {/* QUICK ACCESS */}
      <section className="max-w-7xl mx-auto px-6 py-16" data-testid="quick-access-section">
        <div className="flex items-end justify-between mb-8">
          <h2 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight">{t("home.quick_access")}</h2>
          <div className="h-px flex-1 ml-6 bg-border" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {quickAccess.map((q, i) => (
            <motion.div key={q.to} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: i * 0.05 }} viewport={{ once: true }}>
              <Link to={q.to} data-testid={q.tid}>
                <Card className="group h-full rounded-2xl border-2 border-border/60 hover:border-accent hover:shadow-xl hover:shadow-primary/5 transition-[border-color,box-shadow] duration-300">
                  <CardContent className="p-6">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 ${q.tone === "primary" ? "bg-primary text-primary-foreground" : q.tone === "accent" ? "bg-accent text-accent-foreground" : "bg-secondary"}`}>
                      <q.icon className="w-5 h-5" aria-hidden="true" />
                    </div>
                    <div className="font-heading font-semibold text-lg mb-1">{q.title}</div>
                    <div className="text-sm text-muted-foreground leading-relaxed">{q.desc}</div>
                    <div className="mt-4 text-sm font-medium text-primary flex items-center gap-1 group-hover:gap-2 transition-[gap]">
                      {t("home.access_cta")} <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ACTIVITATS + CAMPANYES GRID */}
      <section className="max-w-7xl mx-auto px-6 py-8 grid lg:grid-cols-3 gap-8" data-testid="news-activities-section">
        {/* NOTÍCIES / CAMPANYES */}
        <div className="lg:col-span-2">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="overline text-accent">{t("home.overline_news")}</div>
              <h2 className="mt-2 text-2xl sm:text-3xl font-heading font-semibold tracking-tight">{t("home.news")}</h2>
            </div>
            <Link to="/directori" className="text-sm text-primary hover:underline" data-testid="home-more-campaigns">{t("dash.view_all")} →</Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {campaigns.slice(0, 4).length === 0 ? (
              <div className="col-span-full text-muted-foreground text-sm">{t("home.no_campaigns")}</div>
            ) : campaigns.slice(0, 4).map((c) => (
              <Card key={c.id} className="rounded-2xl overflow-hidden border-2 border-border/60 group" data-testid={`campaign-card-${c.id}`}>
                {c.image_url && (
                  <div className="aspect-video overflow-hidden">
                    <img src={c.image_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                )}
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 text-[10px] text-accent font-semibold uppercase tracking-wider mb-2">
                    <Megaphone className="w-3 h-3" aria-hidden="true" /> {t("home.campaign_tag")}
                  </div>
                  <h3 className="font-heading font-semibold leading-tight">{c.title}</h3>
                  <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{c.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* ACTIVITATS */}
        <div>
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="overline text-accent">Agenda</div>
              <h2 className="mt-2 text-2xl sm:text-3xl font-heading font-semibold tracking-tight">{t("home.activities")}</h2>
            </div>
          </div>
          <div className="space-y-3" data-testid="home-activities-list">
            {events.length === 0 ? (
              <Card className="rounded-2xl border-2 border-border/60">
                <CardContent className="p-5 text-sm text-muted-foreground">{t("home.no_activities")}</CardContent>
              </Card>
            ) : events.slice(0, 5).map((e) => {
              const d = new Date(e.start_date);
              return (
                <Card key={e.id} className="rounded-2xl border-2 border-border/60" data-testid={`home-event-${e.id}`}>
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-primary text-primary-foreground flex flex-col items-center justify-center text-[10px] font-bold leading-tight shrink-0">
                      <div>{d.toLocaleDateString(i18n.language, { month: "short" }).toUpperCase()}</div>
                      <div className="text-lg">{d.getDate()}</div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate">{e.title}</div>
                      <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                        <CalendarDays className="w-3 h-3" aria-hidden="true" />
                        {d.toLocaleString(i18n.language, { hour: "2-digit", minute: "2-digit" })}
                      </div>
                      {e.location && (
                        <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5 truncate">
                          <MapPin className="w-3 h-3" aria-hidden="true" /> {e.location}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
